<template>
  <v-container data-testid="sale-inventory-create">
    <base-snackbar :errors.sync="verifyErrors" />
    <v-row>
      <v-col class="pa-1" cols="12" lg="4">
        <v-card class="ma-0">
          <v-toolbar color="primary" flat dark class="mb-4">
            <v-card-title> Add Transaction </v-card-title>
          </v-toolbar>
          <v-row class="mx-2">
            <v-col cols="12">
              <v-text-field
                v-model="receiptNo"
                label="Receipt No"
                data-testid="create-input-receipt-no"
                hide-details="auto"
                dense
                outlined
                :error-messages="verifyErrors.receipt_no"
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete
                v-model="selectedStock"
                label="Stock Code"
                data-testid="create-select-stock-code"
                :items="stockList.list"
                :search-input.sync="stockSearchInput"
                :loading="stockList.loading"
                item-value="stockCode"
                no-filter
                :error-messages="verifyErrors.stock_id"
                return-object
                hide-details="auto"
                dense
                outlined
              >
                <template v-slot:selection="{ item }">
                  <span>{{ item.supplier_id }}-{{ item.stock_code }} </span>
                  <span class="blue-grey--text mx-1 text-caption">{{
                    item.product_name
                  }}</span>
                </template>
                <template v-slot:item="{ item }">
                  <div
                    class="d-flex w-100"
                    :style="{ justifyContent: 'space-between', width: '100%' }"
                  >
                    <div class="d-flex">
                      <v-icon v-if="item.promotion"
                      >
                        mdi-information-variant
                      </v-icon
                      >
                      <v-icon v-if="item.wholeSale?.conditions?.length"
                      >
                        mdi-package
                      </v-icon
                      >
                      <span class="blue--text mx-1 text-left"
                      >{{ item.supplier_id }}-{{ item.stock_code }}</span
                      >
                    </div>
                    <span class="mx-1" />
                    <small>{{ item.product_name }}</small>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- WHOLESALE INFO (now emits @apply) -->
            <v-col v-if="selectedStock?.wholeSale?.conditions?.length">
              <whole-sale-info
                :conditions="selectedStock.wholeSale.conditions"
                :quantity="quantity"
                :retail="retail"
              />
            </v-col>

            <v-col v-if="!!selectedStock?.promotion">
              <promo-info
                :promo-item="promoItem"
                :supplier-product="selectedStock"
                :allow-promo.sync="allowPromo"
                @update:allowPromo="allowPromo = $event"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="refNumber"
                data-testid="create-input-senior-id"
                hide-details="auto"
                dense
                outlined
                label="Senior ID"
                :error-messages="verifyErrors.ref_number"
              />
            </v-col>
            <v-col cols="12">
              <v-combobox
                v-if="!defaultConsumer"
                v-model="consumer"
                data-testid="create-combo-consumer"
                hide-details="auto"
                dense
                outlined
                :items="filteredConsumers"
                :search-input.sync="searchConsumer"
                hide-selected
                label="Consumer"
                :error-messages="verifyErrors.consumer"
                no-filter
                return-object
                @keyup.enter="onConsumerComboboxEnter"
              >
                <template #selection="{ item }">
                  <small>({{ item.controlNumber }})</small>&nbsp;<span>{{
                    item.name
                  }}</span>
                </template>
                <template #item="{ item }">
                  <small>({{ item.controlNumber }})</small>&nbsp;<span>{{
                    item.name
                  }}</span>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>
                        No results matching
                        <strong>{{ searchConsumer }}</strong>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-combobox>
              <v-text-field
                v-else
                data-testid="create-input-consumer"
                hide-details="auto"
                dense
                outlined
                label="Consumer"
                :value="defaultConsumer.name"
                readonly
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="dateTransaction"
                data-testid="create-input-transaction-date"
                hide-details="auto"
                dense
                outlined
                label="Transaction Date"
                type="date"
                :min="dateMin"
                :max="dateMax"
                :disabled="!supplierId"
                :error-messages="verifyErrors.date_transaction"
                @focus="refreshTodayDate"
                @blur="clampDateTransaction"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-if="authPermissions.has('sales:unsaved_price_edit')"
                v-model.number="retail"
                data-testid="create-input-srp"
                hide-details="auto"
                dense
                outlined
                label="SRP"
                prefix="₱"
                type="number"
                :disabled="!stockId"
                :error-messages="verifyErrors.retail"
              />
              <v-text-field
                v-else
                data-testid="create-input-srp-readonly"
                hide-details="auto"
                dense
                outlined
                label="SRP"
                prefix="₱"
                type="number"
                :value="retail"
                readonly
                :error-messages="verifyErrors.retail"
              />
            </v-col>

            <v-col cols="12">
              <div class="d-flex align-center" style="gap: 6px">
                <v-text-field
                  v-if="discountMode === 'percent'"
                  v-model.number="itemDiscountPercentage"
                  class="flex-grow-1"
                  data-testid="create-input-discount-percent"
                  hide-details="auto"
                  dense
                  outlined
                  label="Discount/Item"
                  suffix="%"
                  type="number"
                  min="0"
                  max="100"
                  :disabled="
                    !stockId ||
                      ((!!selectedStock?.promotion ||
                        !!selectedStock?.wholeSale?.length) &&
                        allowPromo)
                  "
                  :error-messages="verifyErrors.retail"
                  @keyup="onItemDiscountPercentageChange"
                />
                <v-text-field
                  v-else
                  v-model.number="itemDiscountFixed"
                  class="flex-grow-1"
                  data-testid="create-input-discount-fixed"
                  hide-details="auto"
                  dense
                  outlined
                  label="Discount/Item"
                  prefix="₱"
                  type="number"
                  min="0"
                  :disabled="
                    !stockId ||
                      ((!!selectedStock?.promotion ||
                        !!selectedStock?.wholeSale?.length) &&
                        allowPromo)
                  "
                  :error-messages="verifyErrors.retail"
                  @keyup="onItemDiscountFixedChange"
                />
                <v-btn-toggle
                  v-model="discountMode"
                  data-testid="create-toggle-discount-mode"
                  mandatory
                  dense
                >
                  <v-btn small value="fixed">
                    ₱
                  </v-btn>
                  <v-btn small value="percent">
                    %
                  </v-btn>
                </v-btn-toggle>
              </div>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model.number="discount"
                data-testid="create-input-total-discount"
                hide-details="auto"
                dense
                outlined
                label="Total Discounted Amnt"
                prefix="₱"
                type="number"
                :disabled="
                  !stockId ||
                    ((!!selectedStock?.promotion ||
                      !!selectedStock?.wholeSale?.length) &&
                      allowPromo)
                "
                :error-messages="verifyErrors.retail"
              />
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model.number="quantity"
                data-testid="create-input-quantity"
                hide-details="auto"
                dense
                outlined
                type="number"
                :rules="quantityRules"
                :suffix="unit"
                :placeholder="quantityLeft < 1 ? 'No Stock Available' : ''"
                :disabled="!stockId || zeroStockLocked"
                :error-messages="verifyErrors.quantity"
                @keyup="onQuantityChange"
              >
                <template v-slot:label>
                  <span>Quantity</span>
                  <span v-if="quantityLeft > 0" class="mx-1"
                  >({{ quantityLeft }})</span
                  >
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="notes"
                data-testid="create-input-notes"
                hide-details="auto"
                dense
                outlined
                label="Notes"
                auto-grow
                no-resize
                rows="1"
                :error-messages="verifyErrors.notes"
              />
            </v-col>

            <v-col cols="12">
              <v-btn
                data-testid="create-btn-add-transaction"
                color="green"
                block
                :loading="verifying"
                @click="addTransaction"
              >
                Add Transaction
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col class="pa-1" cols="12" lg="8">
        <v-card class="ma-0">
          <v-toolbar color="primary" dark>
            <span>Unsaved Transactions</span>
            <v-spacer />
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attr }">
                <v-btn
                  class="ma-0"
                  text
                  v-bind="attr"
                  :disabled="!tempList.length"
                  v-on="on"
                  @click="upload"
                >
                  <v-icon>mdi-upload</v-icon>
                </v-btn>
              </template>
              <span>Upload Data</span>
            </v-tooltip>
          </v-toolbar>

          <v-alert
            v-for="(item, index) in splrPdUploadErrors"
            :key="index"
            class="py-0 text-uppercase"
            dense
            type="error"
            outlined
            text
            dismissible
            transition="slide-x-transition"
          >
            {{ item[0] }}
          </v-alert>

          <v-card-text style="overflow: auto">
            <v-responsive min-width="1200">
              <v-data-table
                class="elevation-0"
                data-testid="create-table-unsaved-transactions"
                :headers="headers"
                :items="tempList"
                :loading="splrPdUploading"
                hide-default-footer
                disable-pagination
              >
                <template v-slot:body.append>
                  <tr>
                    <td colspan="8" align="right">
                      <strong>Total</strong>
                    </td>
                    <td align="right">
                      {{ totalAmount.phpFormat() }}
                    </td>
                  </tr>
                </template>

                <template v-slot:item.config="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        class="mx-2"
                        text
                        dark
                        x-small
                        v-bind="attrs"
                        color="red"
                        v-on="on"
                        @click="deleteTempUnit(item.tempId)"
                      >
                        <v-icon dark>
                          mdi-delete
                        </v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </template>

                <template v-slot:item.discount="{ value }">
                  {{ (value || 0).phpFormat() }}
                </template>
                <template v-slot:item.retail="{ value }">
                  {{ (value || 0).phpFormat() }}
                </template>
                <template v-slot:item.totalAmount="{ value }">
                  {{ (value || 0).phpFormat() }}
                </template>
                <template v-slot:item.date_sold="{ value }">
                  {{ (value || '').dateFormat() }}
                </template>
              </v-data-table>
            </v-responsive>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { moneyMixin } from '@/views/dashboard/mixins/helper.mixins'
import { mapState, mapActions } from 'vuex'
import indexKeyGenerator from '@/extra-functions/index-key-generator.extra'
import consumerRepository from '@/repositories/consumer.repository'
import { errorHandler } from '@/extra-functions/store.extra'
import appAxios, { authAxios } from '@/extra-functions/axios.extra'
import {
  ErrorPopup,
  initBarcodeEventHandler,
  LoadingPopup,
  SuccessPopup,
  roundOff,
  useWatchEffect,
  useDebounce,
} from '@/lib/utils'
import PromotionUtil from '../../../../../lib/promotion.util'
import WholeSaleUtil from '../../../../../lib/whole-sale.util'
import DiscountUtil from '../../../../../lib/discount.util'
import { AxiosError } from 'axios'

const [watchEffect, clearWatchEffect] = useWatchEffect()
const barcodeEventHandler = initBarcodeEventHandler()
const debounce = useDebounce()

function currentDate() {
  const date = new Date()
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

const inputVars = () => {
  return {
    receiptNo: null,
    supplierId: null,
    stockSearchInput: null,
    retail: null,
    stockId: '',
    unit: '',
    unitSearchInput: null,
    quantity: null,
    discount: null,
    dateTransaction: currentDate(),
    todayDate: currentDate(),
    notes: null,
    selectedStock: null,
    allowPromo: false,
    refNumber: null, // 👈 added here
  }
}
function consumerVars() {
  return {
    consumer: null,
    consumers: [],
    searchConsumer: null,
    searchingConsumer: false,
    tempConsumers: [],
  }
}

function tableVars() {
  return {
    headers: [
      { text: 'Config', value: 'config' },
      { text: 'Receipt No.', value: 'receipt_no', sortable: false },
      { text: 'Consumer', value: 'consumer_name', sortable: false },
      { text: 'Product Name', value: 'product_name' },
      { Next: 'Brand', value: 'brand_name' },
      { text: 'Discount', value: 'discount' },
      { text: 'SRP', value: 'retail' },
      { text: 'Quantity', value: 'quantity', align: 'right' },
      { text: 'Total Amount', align: 'right', value: 'totalAmount' },
      { text: 'Date', value: 'date_sold' },
    ],
    promoItem: null,
    page: 1,
    itemsPerPage: 10,
  }
}

export default {
  name: 'CreateSaleInventories',
  components: {
    wholeSaleInfo: () => import('../WholeSaleInfo.vue'),
    promoInfo: () => import('../PromoInfo.vue'),
  },
  props: {
    defaultConsumer: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      ...inputVars(),
      ...consumerVars(),
      ...tableVars(),
      stockList: {
        data: [],
        loading: false,
      },
      itemDiscountPercentage: null,
      itemDiscountFixed: null,
      discountMode: 'fixed',
      receiptNoQuery: { getting: false },
      quantityRules: [
        (val) => {
          const ok = this.quantityLeft >= val
          return ok || `Only has ${this.quantityLeft} ${this.unit} in stock`
        },
      ],
      seniorDiscountRate: 0.2,
      discountRates: {
        senior: 0,
        promo: 0,
        wholeSale: 0,
      },
      tempUnits: [],
      panel: true,
      verifyErrors: {},
    }
  },
  created() {
    this.consumer = this.defaultConsumer
    this.onInit()
    console.log(this.tempList)
  },
  mounted() {
    window.addEventListener('keypress', this.handleBarcodeEvent)
  },
  beforeDestroy() {
    window.removeEventListener('keypress', this.handleBarcodeEvent)
    clearWatchEffect()
  },
  computed: {
    ...mapState({
      // verifyErrors: (state) => state.saleInventory.verifyErrors,
      verifying: (state) => state.saleInventory.verifying,
      tempList: (state) => state.saleInventory.tempList,
      unitsUpdated: (state) => state.unit.channelUpdate,
      units: (state) => state.unit.allList,
      uploading: (state) => state.saleInventory.storing,
      uploadSuccess: (state) => state.saleInventory.storeSuccess,
      uploadErrors: (state) => state.saleInventory.storeErrors,
      saleInventoryUpdated: (state) => state.saleInventory.channelUpdate,
      storeId: (state) => state.settings.storeId,
      authToken: (state) => state.auth.token,
      authPermissions: (state) => state.auth.userPermissions,
    }),
    cost() {
      return this.selectedStock?.cost || null
    },
    dateMin() {
      return this.authPermissions.has('sales:date_edit')
        ? null
        : this.todayDate.slice(0, 8) + '01'
    },
    dateMax() {
      return this.authPermissions.has('sales:date_edit') ? null : this.todayDate
    },
    totalAmount() {
      return this.tempList.reduce((total, item) => total + (item.totalAmount || 0), 0)
    },
    zeroStockLocked() {
      return (
        this.quantityLeft <= 0 &&
        !this.authPermissions.has('sales:create_zero_stock')
      )
    },
    quantityLeft() {
      if (!this.selectedStock) return 0
      const stockId = this.stockId
      const supplierId = this.supplierId
      const qRemaining = !Object.entries(this.selectedStock).length
        ? 0
        : this.selectedStock.q_remaining

      const quantity = this.tempList
        .filter((e) => stockId === e.stock_id && supplierId === e.supplier_id)
        .reduce((a, b) => a + (parseFloat(b.quantity) || 0), 0)
      return (parseFloat(qRemaining) || 0) - quantity
    },
    filteredConsumers() {
      const tempConsumers = this.tempConsumers.filter((v) =>
        new RegExp(this.searchConsumer).test(`${v.controlNumber}${v.name}`),
      )
      return this.consumers.concat(tempConsumers)
    },
  },
  watch: {
    searchConsumer() {
      this.fetchConsumers()
    },
    unit(val) {
      const newUnit = this.units.find((e) => e.unit === val)
      if (newUnit === -1 || newUnit === undefined) {
        this.tempUnits.push({
          unit: val,
          description: 'Not in database',
          temp: true,
        })
      }
    },
    stockSearchInput() {
      this.stockList.loading = true
      debounce(this.fetchStocks)
    },
    selectedStock(sp) {
      this.allowPromo = !!sp?.promotion
      this.$delete(this.verifyErrors, 'quantity')
      if (!sp) {
        this.stockId = null
        this.supplierId = null
        this.retail = null
        this.unit = null
        this.discount = 0
        this.itemDiscountPercentage = null
        this.itemDiscountFixed = null
        return
      }
      this.stockId = sp.stock_id
      this.supplierId = sp.supplier_id
      this.retail = sp.retail
      this.unit = sp.unit
      if (this.zeroStockLocked) this.alertZeroStockLocked()
      this.$nextTick(() => {
        this.quantity = null
        return
      })
      // ensure a sensible default qty so tiers can trigger
      this.$nextTick(() => {
        this.applyPromoAndDiscounts()
      })
    },
    // quantity() {
    //   this.$nextTick(() => this.applyPromoAndDiscounts())
    // },
    retail() {
      this.$nextTick(() => this.applyPromoAndDiscounts())
    },
    unitsUpdated() {
      this.fetchUnitsAll()
    },
    supplierUpdated() {
      this.fetchSuppliers({ index: 0, count: 10, search: '' })
    },
    saleInventoryUpdated() {
      this.getLatestReceiptNo()
    },
    refNumber() {
      this.$nextTick(() => this.applyPromoAndDiscounts())
    },
    consumer(v) {
      if (typeof v === 'string') {
        const consumer = {
          id: null,
          name: v,
          controlNumber: 'nw-' + this.tempConsumers.length,
        }
        this.tempConsumers.push(consumer)
        this.consumer = consumer
      }
    },
  },
  methods: {
    ...mapActions({
      fetchSupplierProducts: 'selectedStock/fetch',
      fetchUnitsAll: 'unit/fetchAll',
      checkErrors: 'saleInventory/checkErrors',
      addTempItem: 'saleInventory/addTempItemV2',
      updateTempItem: 'saleInventory/updateTempItem',
      removeTempItem: 'saleInventory/removeTempItem',
      clearTempList: 'saleInventory/clearTempList',
    }),
    onInit() {
      this.fetchSeniorDiscountRate()
      this.fetchConsumers()
      this.dateTransaction = currentDate()
      this.fetchUnitsAll()
      this.fetchStocks()
      this.getLatestReceiptNo()
    },
    fetchSeniorDiscountRate() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => controller.abort())
          const axios = authAxios()
          const { data } = await axios.get(
            '/v2/identity-card-types/senior-discount',
            { signal: controller.signal },
          )
          this.seniorDiscountRate = data?.discountRate ?? 0.2
        } catch (e) {
          console.error(e)
          ErrorPopup.fire(
            'Error',
            'Something went wrong while fetching senior discount rate retrying in 3 sec',
          )
          setTimeout(this.fetchSeniorDiscountRate, 3000)
        }
      }, 'SaleInventoryV2Create_fetchSeniorDiscountRate')
    },
    async fetchStockByBarcode(barcode) {
      try {
        if (!barcode) return
        LoadingPopup.fire('Loading', 'Getting Product Data...')
        const { data } = await appAxios(this.authToken).get(
          '/v2/stock-suppliers/dropdown',
          {
            params: {
              barcode,
              limit: 1,
            },
          },
        )
        if (!data?.data?.length) throw new Error('no stock found')
        const stock = data.data[0]
        this.stockList.list.push(stock)
        LoadingPopup.close()
        return data.data.length > 0
      } catch (e) {
        LoadingPopup.close()
        console.error(e)
        if (e instanceof Error && e.message === 'no stock found') {
          ErrorPopup.fire('Error', e.message + ` on barcode: [${barcode}]`)
          return false
        }
        ErrorPopup.fire('Error', 'Oops something went wrong!!!')
        return false
      }
    },
    handleBarcodeEvent(e) {
      const checkBarcode = (barcode) => {
        const tempListIndex = this.tempList.findIndex(
          (item) => item.barcode === barcode,
        )
        const isInTempList = tempListIndex > -1
        if (isInTempList) {
          const item = this.tempList[tempListIndex]
          const quantity = item.quantity + 1
          this.updateTempItem([
            tempListIndex,
            {
              quantity,
              totalAmount: this.computeAmount(
                item?.retail,
                quantity,
                item?.discount,
              ),
            },
          ])
          return true
        }
        const productListIndex = this.stockList.list.findIndex(
          (item) => item.barcode === barcode,
        )
        const isInProductList = productListIndex > -1

        if (isInProductList) {
          const item = this.stockList.list[productListIndex]
          this.selectedStock = item
          this.$nextTick(() => {
            this.quantity = this.quantity || 1
            this.addTransaction()
          })
          return true
        }
        return false
      }
      barcodeEventHandler(e.key, async (barcode) => {
        const isFound = checkBarcode(barcode)
        if (isFound) return
        const isStockFound = await this.fetchStockByBarcode(barcode)
        if (!isStockFound) return
        checkBarcode(barcode)
      })
    },
    async upload() {
      try {
        LoadingPopup.fire('Uploading', 'storing data to server')
        this.splrPdUploadErrors
        const data = this.tempList.map((e) => {
          return {
            receipt_no: e.receipt_no,
            store_id: e.store_id,
            consumer_name: e.consumer_name,
            stock_id: e.stock_id,
            consumer: e.consumer,
            retail: e.retail,
            supplier_id: e.supplier_id,
            unit: e.unit,
            quantity: e.quantity,
            date_sold: e.date_sold,
            discount: e.discount,
            notes: e.notes,
            cost: e.cost,
            allow_promo: e.allow_promo,
            consumer_id: e.consumer_id,
            ref_number: e.ref_number,
            consumer_control_number: e.consumer_control_number,
          }
        })
        const axios = authAxios()
        const response = await axios.post('/v2/sale-inventories', { data })
        LoadingPopup.close()
        SuccessPopup.fire(
          'Success',
          response.data?.message || 'Sales successfully stored',
        )
        this.$emit('upload-success')
        this.clearInputs()
        this.clearTempList()
        this.onInit()
      } catch (e) {
        LoadingPopup.close()
        this.splrPdUploadErrors = errorHandler(e)
      }
    },
    clearInputs() {
      const inputV = inputVars()
      Object.keys(inputV).forEach((e) => {
        this[e] = inputV[e]
      })
    },
    fetchStocks() {
      watchEffect(async (onCleanup) => {
        try {
          this.stockList.loading = true
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          const axios = authAxios()
          const { data } = await axios.get('/v2/stock-suppliers/dropdown', {
            params: { search: this.stockSearchInput },
            signal: controller.signal,
          })
          this.stockList.list = data.data
        } catch (e) {
          ErrorPopup.fire('Error', 'Failed to get stocks retrying in 3 secs')
          setTimeout(this.fetchStocks, 3000)
        } finally {
          this.stockList.loading = false
        }
      }, 'fetchStocks')
    },
    alertZeroStockLocked() {
      ErrorPopup.fire(
        'No Stock Available',
        `${this.selectedStock?.product_name ||
          'This product'} has 0 or negative stock quantity. You do not have permission to input this data.`,
      )
    },
    refreshTodayDate() {
      this.todayDate = currentDate()
    },
    clampDateTransaction() {
      this.refreshTodayDate()
      if (!this.dateMin || !this.dateTransaction) return
      if (this.dateTransaction < this.dateMin) {
        this.dateTransaction = this.dateMin
      } else if (this.dateTransaction > this.dateMax) {
        this.dateTransaction = this.dateMax
      }
    },
    addTransaction() {
      this.refreshTodayDate()
      if (this.zeroStockLocked) {
        this.verifyErrors = {
          quantity: [
            `No stock available for ${this.selectedStock?.product_name ||
              'this item'}. You do not have permission to sell out-of-stock items.`,
          ],
        }
        this.alertZeroStockLocked()
        return
      }
      if (
        this.dateMin &&
        (this.dateTransaction < this.dateMin ||
          this.dateTransaction > this.dateMax)
      ) {
        this.verifyErrors = {
          date_transaction: [
            'Transaction date must be within the current month (up to today). You do not have permission to set other dates.',
          ],
        }
        return
      }
      watchEffect(async () => {
        try {
          this.verifyErrors = {}
          LoadingPopup.fire('Checking', 'Checking Input Errors')
          const selectedStock = this.selectedStock
          const data = {
            receipt_no: this.receiptNo,
            store_id: this.storeId,
            barcode: this.selectedStock?.barcode || null,
            consumer_name: this.consumer?.name || '',
            stock_id: this.stockId,
            retail: this.retail,
            supplier_id: this.supplierId,
            unit: this.unit,
            discount: this.discount,
            quantity: this.quantity,
            date_sold: this.dateTransaction,
            notes: this.notes,
            cost: this.cost,
            allow_promo: this.allowPromo,
            consumer_id: this.consumer?.id ?? null,
            ref_number: this.refNumber,
          }
          const axios = authAxios()
          const ret = await axios.post(
            '/v2/sale-inventories/check-errors',
            data,
          )
          this.quantity = null
          // self.createNewConsumer(data)
          this.addTempItem(
            Object.assign(
              data,
              {
                stockCode: selectedStock.stock_code,
                brand_name: selectedStock.brand_name,
                productName: selectedStock.product_name,
                totalAmount: this.computeAmount(
                  data?.retail,
                  data?.quantity,
                  data?.discount,
                ),
                consumerControlNumber: this.consumer?.control_number ?? null,
              },
              ret.data,
            ),
          )
          this.selectedStock = null
        } catch (e) {
          console.error('AddTransaction Error', e)
          if (e instanceof AxiosError) {
            this.verifyErrors = e.response?.data?.errors || {}
          }
        } finally {
          LoadingPopup.close()
        }
      }, 'SaleInventoryV2Create_addTransaction')
    },
    dateFormat(ndate) {
      const date = new Date(ndate)
      const year = date.getFullYear()
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      return `${month}/${day}/${year}`
    },
    deleteTempUnit(tempId) {
      this.removeTempItem(tempId)
    },
    initFetchSupplierProducts() {
      this.fetchSupplierProducts(this.supplierId)
    },
    computeAmount(retail = 0, quantity = 0, discount = null) {
      if (!discount) return retail * quantity
      return retail * quantity - discount
    },
    fetchConsumers() {
      if (this.defaultConsumer) return
      watchEffect(async (callback) => {
        try {
          const controller = new AbortController()
          callback(() => {
            controller.abort()
          })
          this.searchingConsumer = true
          const axios = authAxios()
          const { data } = await axios.get('/v2/consumers/dropdown', {
            params: {
              search: this.searchConsumer,
            },
            signal: controller.signal,
          })
          this.consumers = data?.data || []
        } catch (e) {
          console.error(e.message)
          ErrorPopup.fire(
            'Error',
            'Something went wrong while retrieving consumer list retrying in 3 sec...',
          )
          setTimeout(this.fetchConsumers, 3000)
        } finally {
          this.searchingConsumer = false
        }
      }, 'fetchConsumers')
    },
    createNewConsumer(data) {
      if (!data.consumerName) return
      var errorCode = 0
      do {
        consumerRepository
          .new({
            id: indexKeyGenerator(),
            name: data.consumerName,
            storeId: this.storeId,
          })
          .then(() => {})
          .catch(({ response }) => {
            errorCode = response.status
          })
      } while (errorCode === 422)
    },
    getLatestReceiptNo() {
      watchEffect(async (onCleanup) => {
        try {
          const controller = new AbortController()
          onCleanup(() => {
            controller.abort()
          })
          const axios = appAxios(this.authToken)
          if (this.receiptNoQuery.getting) return
          this.receiptNoQuery = { ...this.receiptNoQuery, getting: true }
          const { data } = await axios.get(
            'sale-inventories/latest-receipt-no',
            { signal: controller.signal },
          )
          this.receiptNo = data.toString()
        } catch (e) {
          console.error(e)
          setTimeout(() => this.getLatestReceiptNo, 3000)
        } finally {
          this.receiptNoQuery = { ...this.receiptNoQuery, getting: false }
        }
      }, 'getLatestReceiptNo')
    },
    applyPromoAndDiscounts() {
      if (!this.selectedStock.stockId) {
        this.promoItem = null
        return
      }
      if (
        !this.selectedStock?.promotion?.promoItem &&
        !this.selectedStock?.wholeSale
      ) {
        this.promoItem = null
        return
      }
      console.log('applying promo and discounts')
      const promotionUtil = new PromotionUtil(
        this.selectedStock.stockId,
        this.selectedStock?.promotion?.promoItem,
      )
      promotionUtil.setQuantity(this.quantity)
      this.promoItem = promotionUtil.getPromoItem()

      const wholeSaleUtil = new WholeSaleUtil(
        this.selectedStock?.wholeSale?.conditions || [],
      )
      wholeSaleUtil.setQuantity(this.quantity)
      wholeSaleUtil.setRetail(this.retail)
      const seniorDiscountRate =
        Number(this.refNumber && this.seniorDiscountRate) || 0
      const discountUtil = new DiscountUtil(
        promotionUtil.getDiscount(),
        wholeSaleUtil.getDiscount(),
        seniorDiscountRate,
      )
      discountUtil.setQuantity(this.quantity)
      discountUtil.setRetail(this.retail)
      this.itemDiscountPercentage =
        Math.round(discountUtil.getDiscountRate() * 1000) / 10
      this.itemDiscountFixed =
        Math.round(discountUtil.getDiscountRate() * this.retail * 100) / 100
      this.discount = Math.round(discountUtil.getDiscount() * 100) / 100
    },
    /**
     * Description
     * @param {KeyboardEvent} [e]
     * @returns {any}
     */
    onItemDiscountPercentageChange(e) {
      if (this.allowPromo) return
      const val =
        e instanceof KeyboardEvent
          ? e.target?.value || 0
          : this.itemDiscountPercentage || 0
      this.discount = roundOff((val / 100) * this.retail * this.quantity)
      this.itemDiscountFixed = roundOff((val / 100) * this.retail)
    },
    /**
     * Description
     * @param {KeyboardEvent} [e]
     * @returns {any}
     */
    onItemDiscountFixedChange(e) {
      if (this.allowPromo) return
      const val =
        e instanceof KeyboardEvent
          ? e.target?.value || 0
          : this.itemDiscountFixed || 0
      this.discount = roundOff(val * this.quantity)
      this.itemDiscountPercentage = this.retail
        ? Math.round((val / this.retail) * 1000) / 10
        : null
    },
    onQuantityChange() {
      try {
        if (!this.allowPromo) {
          if (this.discountMode === 'fixed') {
            this.onItemDiscountFixedChange()
          } else {
            this.onItemDiscountPercentageChange()
          }
          return
        }
        this.applyPromoAndDiscounts()
      } catch (e) {
        console.error('error', e)
      }
    },
  },
  mixins: [moneyMixin],
}
</script>

<style lang="scss" scoped>
.truncate {
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
